import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { analysisService } from '../../services/analysisService';
import { corpusService } from '../../services/corpusService';
import toast from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import TextAnalysisList from '../../components/analysis/TextAnalysisList';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileAlt,
  faBalanceScale,
  faCoins,
  faBuilding,
  faGraduationCap,
  faIndustry,
  faGlobe,
  faLaptopCode,
  faHospital
} from '@fortawesome/free-solid-svg-icons';
import { CorpusSector } from '../../types';

// Map sector codes to Font Awesome icons with unique, professional icons
const getSectorIcon = (sector: CorpusSector) => {
  const iconMap: Record<string, any> = {
    'GENERAL': faFileAlt,
    'LEGAL': faBalanceScale,
    'FINANCE': faCoins,
    'finanzas': faCoins, // Added lowercase variant
    'ADMIN': faBuilding,
    'HEALTH': faHospital,
    'EDUCATION': faGraduationCap,
    'INDUSTRY': faIndustry,
    'TECH': faLaptopCode,
  };

  return iconMap[sector.code] || faGlobe;
};

export default function TextAnalysisPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('new');
  const [formData, setFormData] = useState({
    text: '',
    sectorIds: [] as number[], // Changed to array for multiple selection
    title: '',
  });

  const { data: sectorsData } = useQuery({
    queryKey: ['sectors'],
    queryFn: corpusService.getSectors,
  });

  // Find General sector ID and initialize it
  const generalSector = sectorsData?.sectors.find(s => s.code === 'GENERAL');
  const generalSectorId = generalSector?.id;

  // Auto-select General sector when it's loaded
  useEffect(() => {
    if (generalSectorId && !formData.sectorIds.includes(generalSectorId)) {
      setFormData(prev => ({
        ...prev,
        sectorIds: [generalSectorId]
      }));
    }
  }, [generalSectorId]);

  const analyzeMutation = useMutation({
    mutationFn: analysisService.analyzeText,
    onSuccess: (data) => {
      toast.success('Análisis completado');
      navigate(`/analysis/${data.analysis.id}`);
    },
    onError: () => {
      toast.error('Error al analizar el texto');
    },
  });

  const toggleSector = (sectorId: number) => {
    // Prevent deselecting General sector
    if (sectorId === generalSectorId) {
      return;
    }

    setFormData(prev => {
      const isSelected = prev.sectorIds.includes(sectorId);
      return {
        ...prev,
        sectorIds: isSelected
          ? prev.sectorIds.filter(id => id !== sectorId) // Remove if already selected
          : [...prev.sectorIds, sectorId] // Add if not selected
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Strip HTML tags for validation
    const plainText = formData.text.replace(/<[^>]*>/g, '').trim();

    if (plainText.length < 10) {
      toast.error('El texto debe tener al menos 10 caracteres');
      return;
    }

    if (formData.sectorIds.length < 2) {
      toast.error('Debes seleccionar al menos un sector adicional además de General');
      return;
    }

    // Send complete array of selected sector IDs
    // Backend now supports multiple sectors via sectorIds array
    const submitData = {
      text: formData.text,
      title: formData.title,
      sectorIds: formData.sectorIds,
      // Also send first sector for backward compatibility
      sectorId: formData.sectorIds[0]
    };

    analyzeMutation.mutate(submitData);
  };

  // Calculate word count from HTML content (strip tags first)
  const plainText = formData.text.replace(/<[^>]*>/g, '').trim();
  const wordCount = plainText.match(/\S+/g)?.length || 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-blue-600 rounded-xl p-8 shadow-lg">
        <h1 className="text-3xl font-bold mb-2 text-white">Análisis de Texto</h1>
        <p className="text-white/90">
          Analiza tu texto para evaluar su claridad y cumplimiento con nivel B2 del MCERL
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="new">Nuevo Análisis</TabsTrigger>
          <TabsTrigger value="history">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-6">
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sector Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Seleccionar Sectores *</CardTitle>
                <CardDescription>
                  Elige uno o varios sectores para analizar el texto con vocabulario especializado.
                  {formData.sectorIds.length >= 2 ? (
                    <span className="block mt-1 text-primary font-medium">
                      {formData.sectorIds.length} sectores seleccionados (General + {formData.sectorIds.length - 1} adicional{formData.sectorIds.length > 2 ? 'es' : ''})
                    </span>
                  ) : (
                    <span className="block mt-1 text-destructive font-medium">
                      Debes seleccionar al menos un sector adicional
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {sectorsData?.sectors
                    .slice()
                    .sort((a, b) => {
                      // General always first
                      if (a.code === 'GENERAL') return -1;
                      if (b.code === 'GENERAL') return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map((sector) => {
                      const isSelected = formData.sectorIds.includes(sector.id);
                      const isGeneral = sector.code === 'GENERAL';
                      return (
                        <button
                          key={sector.id}
                          type="button"
                          onClick={() => toggleSector(sector.id)}
                          disabled={isGeneral}
                          className={`p-5 rounded-lg border-2 transition-all hover:shadow-md relative ${
                            isSelected
                              ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20'
                              : 'border-border hover:border-primary/50 bg-card'
                          } ${isGeneral ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'}`}
                        >
                          {isSelected && (
                            <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                              ✓
                            </div>
                          )}
                          {isGeneral && (
                            <div className="absolute top-2 left-2 bg-blue-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">
                              OBLIGATORIO
                            </div>
                          )}
                          <div className={`text-3xl mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                            <FontAwesomeIcon icon={getSectorIcon(sector)} />
                          </div>
                          <div className={`font-semibold text-sm ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                            {sector.name}
                          </div>
                        </button>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            {/* Title */}
            <Card>
              <CardContent className="pt-6">
                <label className="block text-sm font-medium mb-2">Título del análisis (opcional)</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent bg-background"
                  placeholder="Ej: Contrato de hipoteca"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </CardContent>
            </Card>

            {/* Text Input */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium">Texto a analizar *</label>
                  <span className="text-sm text-muted-foreground">{wordCount} palabras</span>
                </div>

                <RichTextEditor
                  content={formData.text}
                  onChange={(content) => setFormData({ ...formData, text: content })}
                  placeholder="Escribe o pega aquí el texto que deseas analizar..."
                />

                <p className="text-xs text-muted-foreground mt-2">
                  Mínimo 10 caracteres. Para mejores resultados, usa textos de al menos 100 palabras.
                </p>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                type="submit"
                className="flex-1"
                disabled={analyzeMutation.isPending || formData.text.length < 10 || formData.sectorIds.length < 2}
              >
                {analyzeMutation.isPending ? 'Analizando...' : 'Analizar Texto'}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <TextAnalysisList />
        </TabsContent>
      </Tabs>
    </div>
  );
}
